import React, { useEffect, useState } from "react";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";
import ThemeBarChart from "../shared/ThemeBarChart";
import BigFiveChart from "../shared/BigFiveChart";
import big5Descriptions from "../../json/big5_descriptions.json";
import threeLetterDescriptions from "../../json/three_letter_descriptions.json";
import gotDescriptions from "../../json/GOT_descriptions.json";

const Results = ({ userId: propUserId }) => {
    useAxiosInterceptor();

    // Use the provided userId, fallback to localStorage only if no prop is passed
    const [userId] = useState(propUserId || localStorage.getItem("userId"));
    const [themeScores, setThemeScores] = useState([]);
    const [subscaleScores, setSubscaleScores] = useState([]);
    const [bigFiveScores, setBigFiveScores] = useState([]);
    const [firstName, setFirstName] = useState("");
    const [loading, setLoading] = useState(true);

    // Define bigFiveColors for Big Five traits
    const bigFiveColors = {
        "Conscientiousness": "#ff66e6",  // Pink
        "Extroversion": "#008000",       // Green
        "Emotional Stability": "#4444CC", // Purple
        "Agreeableness": "#FFD700",      // Yellow
        "Intellect": "#32CEE5",          // Sky Blue
    };

    // Optionally, define themeColors if needed for table rows (assumes similar structure as Assessment)
    const themeColors = {
        "Investigative": "#DC143C",
        "Realistic": "#9370DB",
        "Conventional": "#FFA500",
        "Social": "#90EE90",
        "Artistic": "#FF7F50",
        "Enterprising": "#87CEEB",
    };
    useEffect(() => {
        if (userId) {
            api.put(`/results/${userId}`).catch(error => {
                    console.error("Error updating results viewed:", error);
                });
        }
    }, [userId]);
    useEffect(() => {
        const fetchResults = async () => {
            try {
                const response = await api.get(`/results/${userId}`);
                setThemeScores(response.data.occupationalThemeScores || []);
                setSubscaleScores(response.data.occupationalSubscaleScores || []);
                setBigFiveScores(response.data.bigFiveScores || []);
                setFirstName(response.data.firstName || "");
            } catch (error) {
                console.error("❌ Error fetching results:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [userId]);

    // Function to categorize Big Five scores into high, medium, and low
    const categorizeBigFive = (trait, score) => {
        if (score >= 67) return big5Descriptions.english[trait].high;
        if (score >= 34) return big5Descriptions.english[trait].medium;
        return big5Descriptions.english[trait].low;
    };

    if (loading)
        return <div className="text-white text-center">Loading results...</div>;

    // Sort themes from highest to lowest score
    const sortedThemes = [...themeScores].sort((a, b) => b.score - a.score);
    const topThreeThemes = sortedThemes.slice(0, 3);
    const bottomThreeThemes = sortedThemes.slice(3);

    // Determine the three-letter occupational theme code
    const threeLetterCode = topThreeThemes.map(t => t.theme[0]).join("");
    const topThreeDescription = threeLetterDescriptions.english[threeLetterCode] || {
        "Your past": "Description not found.",
        "How you see yourself": "Description not found.",
        "How others see you": "Description not found."
    };

    return (
        <div className="mt-2 flex flex-col items-center justify-center min-h-screen p-4 bg-darkblue">
            <h1 className="text-3xl font-bold text-orange mb-4">
                Career Mission Results {firstName && `for ${firstName}`}
            </h1>

            <div className="max-w-2xl bg-bluegray shadow-md p-4 rounded-lg text-white text-lg text-center mb-6">
                <p className="mt-2">
                    Thank you{firstName ? `, ${firstName}` : ""}, for completing your assessment! 🎉 We're thrilled to have you on this journey with us.
                    Your results are being processed, and soon you'll see how your unique strengths and preferences
                    let us find the perfect job openings for you.
                </p>
                <p className="mt-2 font-bold text-2xl">
                    Be on the lookout for those matches. We can't wait!
                </p>
            </div>

            <div className="text-orange text-3xl text-center mb-6">
                <p><strong>You are:</strong> {threeLetterCode}</p>
            </div>

            <div className="flex flex-col lg:flex-row w-full gap-4">
            {/* Left Column: Occupational Themes */}
                <div className="flex flex-col gap-4 w-full lg:w-1/2">
                <div className="bg-bluegray shadow-md p-4 rounded-lg relative-30">
                        <h2 className="text-lg font-bold text-white mb-2 text-center">
                            Occupational Theme Scores
                        </h2>
                        <ThemeBarChart themeScores={themeScores} subscaleScores={subscaleScores} />
                    </div>

                    <div className="bg-bluegray shadow-md p-4 rounded-lg">
                        <h3 className="text-lg font-bold text-white mb-2 text-center">
                            Interpretation of Your Top Three Themes
                        </h3>
                        <p className="text-white">
                            <strong>In your past:</strong> {topThreeDescription["Your past"]}
                        </p>
                        <p className="text-white">
                            <strong>You probably see yourself as:</strong> {topThreeDescription["How you see yourself"]}
                        </p>
                        <p className="text-white">
                            <strong>Others probably see you as:</strong> {topThreeDescription["How others see you"]}
                        </p>
                    </div>

                    <div className="bg-bluegray shadow-md p-4 rounded-lg overflow-x-auto">
                        <h3 className="text-lg font-bold text-white mb-2 text-center">
                            Top Three Themes Breakdown
                        </h3>
                        <table className="w-full text-white border border-white text-sm">
                            <thead className="bg-gray-800">
                            <tr>
                                <th className="p-2">Theme</th>
                                <th className="p-2">Code</th>
                                <th className="p-2">Interests</th>
                                <th className="p-2">Work Activities</th>
                                <th className="p-2">Potential Skills</th>
                                <th className="p-2">Values</th>
                            </tr>
                            </thead>
                            <tbody>
                            {topThreeThemes.map((theme, index) => {
                                const themeData = gotDescriptions.english[theme.theme[0]];
                                const themeColor = themeData && themeData.Theme
                                    ? themeColors[themeData.Theme] || "#FFFFFF"
                                    : "#FFFFFF";
                                return (
                                    <tr key={index} className="border-b border-white">
                                        <td className="p-2 font-bold text-lg" style={{ color: themeColor }}>
                                            {themeData ? themeData.Theme : "N/A"}
                                        </td>
                                        <td className="p-2 font-bold text-lg" style={{ color: themeColor }}>
                                            {themeData ? themeData.Code : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData.Interests : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData["Work Activities"] : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData["Potential Skills"] : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData.Values : "N/A"}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-bluegray shadow-md p-4 rounded-lg overflow-x-auto">
                        <h3 className="text-lg font-bold text-white mb-2 text-center">
                            You scored lower on these themes:
                        </h3>
                        <table className="w-full text-white border border-white text-sm">
                            <thead className="bg-gray-800">
                            <tr>
                                <th className="p-2">Theme</th>
                                <th className="p-2">Code</th>
                                <th className="p-2">Interests</th>
                                <th className="p-2">Work Activities</th>
                                <th className="p-2">Potential Skills</th>
                                <th className="p-2">Values</th>
                            </tr>
                            </thead>
                            <tbody>
                            {bottomThreeThemes.map((theme, index) => {
                                const themeData = gotDescriptions.english[theme.theme[0]];
                                const themeColor = themeData && themeData.Theme
                                    ? themeColors[themeData.Theme] || "#FFFFFF"
                                    : "#FFFFFF";
                                return (
                                    <tr key={index} className="border-b border-white">
                                        <td className="p-2 font-bold text-lg" style={{ color: themeColor }}>
                                            {themeData ? themeData.Theme : "N/A"}
                                        </td>
                                        <td className="p-2 font-bold text-lg" style={{ color: themeColor }}>
                                            {themeData ? themeData.Code : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData.Interests : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData["Work Activities"] : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData["Potential Skills"] : "N/A"}
                                        </td>
                                        <td className="p-2">
                                            {themeData ? themeData.Values : "N/A"}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* Right Column: Big Five Personality Analysis */}
                <div className="flex flex-col gap-4 w-full lg:w-1/2">
                <div className="bg-bluegray shadow-md p-4 rounded-lg">
                        <h2 className="text-lg font-bold text-white mb-2 text-center relative-10">
                            Big Five Personality Traits
                        </h2>
                        <BigFiveChart bigFiveScores={bigFiveScores} />
                    </div>

                    {bigFiveScores.map((trait, index) => {
                        const traitDetails = categorizeBigFive(trait.subscale, trait.score);
                        const traitColor = bigFiveColors[trait.subscale] || "#FFFFFF";
                        return (
                            <div key={index} className="bg-bluegray shadow-md p-4 rounded-lg">
                                <h3 className="text-xl font-bold mb-2" style={{ color: traitColor }}>
                                    {trait.subscale}
                                </h3>
                                <p className="text-white">
                                    <strong>Your score:</strong> {trait.score.toFixed(1)}
                                </p>
                                <p className="text-white">{traitDetails.description}</p>
                                <p className="text-white">
                                    <strong>Career Implications:</strong> {traitDetails.career_implications}
                                </p>
                                <p className="text-white">
                                    <strong>Cautions:</strong> {traitDetails.cautions}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Results;
